import {
  useDeletePaymentMode,
  useGetPaymentMode,
  useUpdatePaymentModeStatus,
} from "@features/cx/settings/hooks";
import { toast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@shared/ui/alert-dialog";
import { Switch } from "@shared/ui/switch";
import {
  Banknote,
  CreditCard,
  Edit,
  Plus,
  Wallet,
  Trash2,
  Eye,
} from "lucide-react";
import { useState } from "react";
import { PaymentModeForm } from "./paymentModeForm/paymentModeForm";
import { PaymentModeView } from "./paymentModeForm/view/paymentModeView";

interface CustomField {
  key: string;
  id: string;
  label: string;
  fieldType: "text" | "number" | "date";
}

interface PaymentMode {
  code: string;
  name: string;
  type: string;
  isActive: boolean;
  transactionFee?: number;
  description?: string;
  minAmount?: number;
  maxAmount?: number;
  customFields?: CustomField[];
}

const modeIcons = {
  cash: Banknote,
  card: CreditCard,
  digital: Wallet,
  transfer: Banknote,
};

// Codes for payment modes that cannot be edited or deleted
const NON_EDITABLE_CODES = ["Cash#4", "Cheque#33", "Online#5"];

export function PaymentModesSettings() {
  const remote_utility_id = useRemoteUtilityId();
  const { data: paymentModes } = useGetPaymentMode();
  const updatePaymentModeStatus = useUpdatePaymentModeStatus();
  const deletePaymentMode = useDeletePaymentMode();
  const [modes, setModes] = useState<PaymentMode[]>(paymentModes);
  const [showDialog, setShowDialog] = useState(false);
  const [editingMode, setEditingMode] = useState<PaymentMode | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [modeToDelete, setModeToDelete] = useState<PaymentMode | null>(null);

  // New state for view dialog
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingMode, setViewingMode] = useState<PaymentMode | null>(null);

  const handleAddMode = () => {
    setEditingMode(null);
    setShowDialog(true);
  };

  const handleEditMode = (mode: PaymentMode) => {
    setEditingMode(mode);
    setShowDialog(true);
  };

  // New function to handle view mode
  const handleViewMode = (mode: PaymentMode) => {
    setViewingMode(mode);
    setShowViewDialog(true);
  };

  const handleDeleteMode = (mode: PaymentMode) => {
    setModeToDelete(mode);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (modeToDelete) {
      const payloadDelete = {
        code: modeToDelete.code,
        remote_utility_id: Number(remote_utility_id),
      };

      deletePaymentMode.mutateAsync(payloadDelete);

      console.log("Deleting payment mode:", payloadDelete);

      setShowDeleteDialog(false);
      setModeToDelete(null);

      toast({
        title: "Payment Mode Deleted",
        description: `${modeToDelete.name} has been deleted successfully.`,
      });
    }
  };

  const handleToggleMode = (code: string, checked: boolean) => {
    const mode = paymentModes?.find((mode) => mode.code === code);
    if (!mode) {
      toast({
        title: "Error",
        description: "Mode not found",
        variant: "destructive",
      });
      return;
    }
    const payload = {
      code: mode.code,
      is_active: checked,
      remote_utility_id: Number(remote_utility_id),
    };
    updatePaymentModeStatus.mutateAsync(payload);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingMode(null);
  };

  // New function to handle view dialog close
  const handleCloseViewDialog = () => {
    setShowViewDialog(false);
    setViewingMode(null);
  };

  // Check if a payment mode is editable/deletable
  const isModeEditable = (code: string) => {
    return !NON_EDITABLE_CODES.includes(code);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <p className="card-heading-text">Payment Modes Configuration</p>
          <p className="page-subtitle">
            Configure available payment modes and their custom fields
          </p>
        </div>
        <Button onClick={handleAddMode}>
          <Plus className="mr-2 h-4 w-4" />
          Add Payment Mode
        </Button>
      </div>

      <div className="space-y-4 mt-5">
        {paymentModes?.map((mode) => {
          const IconComponent =
            modeIcons[mode.type as keyof typeof modeIcons] || Banknote;
          const canEdit = isModeEditable(mode.code);

          return (
            <div
              key={mode.code}
              className="flex items-center justify-between p-4 border rounded-lg"
            >
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <IconComponent className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3>{mode.name}</h3>
                    <Badge
                      variant={mode.isActive ? "secondary" : "outline"}
                      className={
                        mode.isActive ? "bg-green-100 text-green-800" : ""
                      }
                    >
                      {mode.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{mode.remark}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground"></div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={mode.isActive}
                  onCheckedChange={(checked) =>
                    handleToggleMode(mode.code, checked)
                  }
                />
                {/* View Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewMode(mode)}
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  // disabled={!canEdit}
                  onClick={() => handleEditMode(mode)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!canEdit}
                  onClick={() => handleDeleteMode(mode)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <PaymentModeForm
        isOpen={showDialog}
        onClose={handleCloseDialog}
        editingMode={editingMode}
        canEdit={editingMode ? isModeEditable(editingMode.code) : true}
      />

      {/* View Dialog */}
      <PaymentModeView
        isOpen={showViewDialog}
        onClose={handleCloseViewDialog}
        viewingMode={viewingMode}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Payment Mode</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{modeToDelete?.name}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
